import { Controller } from "@hotwired/stimulus"
import "ninja-keys"
import { cachedFetch } from "../utils/cached_fetch"

const fetchItems = cachedFetch({
  maxAge: 5 * 60 * 1000,
  init: { headers: { 'Accept': 'application/json' } },
  read: response => response.json()
})

/**
 * Command Palette Controller
 *
 * Provides a CMD+K (Mac) / CTRL+K (Windows/Linux) command palette for quick navigation.
 * Uses ninja-keys library (https://github.com/ssleptsov/ninja-keys)
 */
export default class extends Controller {
  static values = {
    itemsUrl: String,
    searchPath: String,
    assistantPath: String
  }

  connect() {
    this.baseItems = []
    this.inputMode = null
    this.originalPlaceholder = this.element.getAttribute('placeholder')

    // Listen for input changes to update search item
    this.handleInputBound = this.handleInput.bind(this)
    this.handleCloseBound = this.handleClose.bind(this)

    this.element.addEventListener('change', this.handleInputBound)
    this.element.addEventListener('close', this.handleCloseBound)

    // Load items asynchronously to avoid blocking initial page render
    this.loadItems()
  }


  disconnect() {
    this.element.removeEventListener('change', this.handleInputBound)
    this.element.removeEventListener('close', this.handleCloseBound)
  }

  open() {
    this.element.open()
  }

  async loadItems() {
    try {
      const items = await fetchItems(this.itemsUrlValue)
      this.baseItems = items.map(item => this.transformItem(item))
      this.element.data = this.baseItems
    } catch (error) {
      console.error('Failed to load command palette items:', error)
    }
  }

  transformItem(item) {
    const transformed = { ...item }

    if (item.handler) {
      const handlerData = item.handler
      transformed.handler = () => {
        if (handlerData.type === "navigate") {
          this.element.close()
          Turbo.visit(handlerData.path)
        } else if (handlerData.type === "turbo_frame") {
          this.element.close()
          Turbo.visit(handlerData.path, { frame: handlerData.frame })
        } else if (handlerData.type === "open") {
          this.element.close()
          window.open(handlerData.url, "_blank")
        } else if (handlerData.type === "focus_search") {
          this.enterInputMode('search', {
            filterClass: handlerData.filter_class,
            label: handlerData.filter_label,
            icon: handlerData.filter_icon,
            placeholder: `Buscar en ${handlerData.filter_label}...`
          })
        } else if (handlerData.type === "assistant_question") {
          this.enterInputMode('assistant', {
            label: handlerData.label,
            icon: handlerData.icon,
            placeholder: handlerData.label
          })
        }
      }
    }

    return transformed
  }

  enterInputMode(mode, options) {
    this.inputMode = { mode, ...options }

    // Update placeholder
    this.element.setAttribute('placeholder', options.placeholder)

    // Keep palette open and update after a brief delay (to override ninja-keys default close)
    setTimeout(() => {
      // Reopen if it closed
      if (!this.element.opened) {
        this.element.open()
      }

      // Go back to root
      this.element.setParent(null)

      // Update items to show input mode
      this.updateInputModeItems('')

      // Focus the input
      const input = this.element.shadowRoot?.querySelector('input')
      if (input) {
        input.value = ''
        input.focus()
      }
    }, 10)
  }

  handleInput(event) {
    if (!this.inputMode) return

    // ninja-keys 'change' event has detail.search with the current input value
    const query = event.detail?.search ?? ''
    this.updateInputModeItems(query)
  }

  updateInputModeItems(query) {
    if (!this.inputMode) return

    const { mode, label, icon, filterClass } = this.inputMode

    if (query.trim()) {
      let title, handler

      if (mode === 'search') {
        title = `Buscar "${query}" en ${label}`
        handler = () => {
          this.element.close()
          const searchUrl = `${this.searchPathValue}?search[title]=${encodeURIComponent(query)}&search[filter_class]=${encodeURIComponent(filterClass)}`
          Turbo.visit(searchUrl)
        }
      } else if (mode === 'assistant') {
        title = query
        handler = () => {
          this.element.close()
          this.submitAssistantQuestion(query)
        }
      }

      const actionItem = {
        id: 'execute-action',
        title: title,
        icon: icon,
        handler: handler
      }
      this.element.data = [actionItem]
    } else {
      // Show placeholder item
      const placeholderItem = {
        id: 'input-placeholder',
        title: label,
        icon: icon,
      }
      this.element.data = [placeholderItem]
    }
  }

  handleClose() {
    // Reset input mode when palette closes (with delay to allow reopen)
    setTimeout(() => {
      if (!this.element.opened && this.inputMode) {
        this.inputMode = null
        this.element.setAttribute('placeholder', this.originalPlaceholder)
        this.element.data = this.baseItems
      }
    }, 50)
  }

  submitAssistantQuestion(query) {
    // Create a form to POST the new conversation
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = this.assistantPathValue
    form.style.display = 'none'

    // CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    // Message question
    const messageInput = document.createElement('input')
    messageInput.type = 'hidden'
    messageInput.name = 'message[question]'
    messageInput.value = query
    form.appendChild(messageInput)

    document.body.appendChild(form)
    form.submit()
  }
}
